import React, { useEffect, useState } from 'react';
import { loadPaidClaimsDetails, loadRxDetails } from '../Services/loadOrder';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuBar from '../components/common/MenuBar';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import "../assets/css/home.css";
import toast, { Toaster } from 'react-hot-toast';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const PaidTaskGrid = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { checkNumber, contract } = location.state || {};

  const columns = [
    { field: 'authNo', headerName: 'Auth No',flex:1, headerClassName: 'pink-header', searchField: "AuthNo" },
    { field: 'from', headerName: 'From', flex:1, headerClassName: 'pink-header', searchField: 'From' },
    { field: 'submittedDate', headerName: 'Submitted Date',flex:1, headerClassName: 'pink-header', searchField: 'SubmittedDate' },
    { field: 'paidDate', headerName: 'Paid Date', flex:1, headerClassName: 'pink-header', searchField: 'PaidDate' },
    { field: 'memberId', headerName: 'Member ID', flex:1, headerClassName: 'pink-header', searchField: 'MemberID' },
    { field: 'lastName', headerName: 'Last Name', flex:1, headerClassName: 'pink-header', searchField: 'Last Name' },
    { field: 'firstName', headerName: 'First Name',flex:1, headerClassName: 'pink-header', searchField: 'First Name' },
    { field: 'contract', headerName: 'Contract', flex:1, headerClassName: 'pink-header', searchField: 'Contract',
       renderCell: (params) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.3,
        padding: '10px 2px',
        width: '100%',
      }}
    >
      {params.value}
    </div>
  )
     },
  ];

  useEffect(() => {
    const searchFields = columns.map(col => ({
      label: col.headerName,
      field: col.searchField || col.field
    }));
    setNames(searchFields);
  }, []);

  // ✅ Fetch all data initially
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const pharmacyId = user?.pharmacyId;
        const userId = user?.userId;

        if (!pharmacyId) {
          navigate('/error-page');
          return;
        }

        const orderResponse = await loadPaidClaimsDetails(checkNumber, pharmacyId, contract, "", "");
        if (orderResponse.success) {
          const formattedRows = orderResponse.data.map((item, index) => ({
            id: index + 1, // ✅ ID is now index + 1
            authNo: item.AuthNo,
            from: item.From,
            submittedDate: item.SubmittedDate ? new Date(item.SubmittedDate).toLocaleDateString() : '',
            paidDate: item.PaidDate ? new Date(item.PaidDate).toLocaleDateString() : '',
            memberId: item.MemberID,
            lastName: item?.['Last Name'],
            firstName: item?.['First Name'],
            contract: item.Contract,
            taskId: item.TaskID,
            pharmacyId,
            userId
          }));
          setRows(formattedRows);
        }
      } catch (error) {
        toast.error("Error loading paid claims data.");
        navigate('/error-page');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [checkNumber, contract, navigate]);

  // ✅ Handle search
  const handleSearch = async () => {
    if (!selectedName || !searchText) {
      toast.error("Please select a value and enter text to search.");
      return;
    }

    setSearchLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const pharmacyId = user?.pharmacyId;
      const userId = user?.userId;

      const orderResponse = await loadPaidClaimsDetails(checkNumber, pharmacyId, contract, selectedName, searchText);
      if (orderResponse.success) {
        const formattedRows = orderResponse.filterData.map((item, index) => ({
          id: index + 1, // ✅ Keep same logic for search
          authNo: item.AuthNo,
          from: item.From,
          submittedDate: item.SubmittedDate ? new Date(item.SubmittedDate).toLocaleDateString() : '',
          paidDate: item.PaidDate ? new Date(item.PaidDate).toLocaleDateString() : '',
          memberId: item.MemberID,
          lastName: item?.['Last Name'],
          firstName: item?.['First Name'],
          contract: item.Contract,
          taskId: item.TaskID,
          pharmacyId,
          userId
        }));
        setRows(formattedRows);
      } else {
        setRows([]);
        toast.error("No data found for the search.");
      }
    } catch (error) {
      toast.error("Error searching paid claims.");
    } finally {
      setSearchLoading(false);
    }
  };

  // ✅ Handle row click
  const handleRowClick = async (params) => {
    const { contract, taskId, pharmacyId, userId } = params.row;
    if (!contract || !taskId || !pharmacyId || !userId) {
      toast.error("Missing required task or user data.");
      navigate('/error-page');
      return;
    }

    try {
      const rxResponse = await loadRxDetails(taskId, contract, pharmacyId, userId);
      if (rxResponse?.data?.TaskDetail) {
        navigate('/paid-task-open-orders', {
          state: { taskId, contract, pharmacyId, userId, rxData: rxResponse }
        });
      } else {
        toast.error("No prescription data found.");
      }
    } catch (err) {
      toast.error("Error loading prescription details.");
      navigate('/error-page');
    }
  };

  if (loading) {
    return (
      <div>
        <MenuBar />
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <CircularProgress sx={{ color: '#ec008c' }} />
          <span style={{ marginTop: '16px', color: '#ec008c', fontSize: '16px', fontWeight: 500 }}>
            Your request is being processed...
          </span>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <MenuBar />
      <Toaster position='top-right' />
      <div className='top-page-option'>
        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center' }}>
          <a href="#/paid-claims" style={{ textDecoration: 'none', color: '#ec088c', display: 'inline-flex', alignItems: 'center' }}>
            <ChevronLeft color='#ec088c' size={18} />
            <span style={{ marginLeft: '5px' }}>Back</span>
          </a>
        </div>

        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>Dispensed Orders</span>
          <ChevronRight color='#ec088c' size={18} style={{ position: 'relative', top: '2px' }} />
          <span>Paid Claims</span>
       

        <div style={{ display: 'flex', justifyContent:'right',alignItems: 'center', gap: '8px',marginLeft:'auto' ,marginRight:'72px'}}>
         
          <FormControl size="small" sx={{ minWidth: 100, height: '30px', background: '#fff', borderRadius: '4px', marginRight: '10px' }}>
  <InputLabel sx={{ fontFamily: 'Calibri', marginBottom: '10px', background: '#fff', color: 'black', fontSize: '12px' }}>
    --Select--
  </InputLabel>
  <Select
    value={selectedName}
    onChange={(e) => setSelectedName(e.target.value)}
    label="Select"
    sx={{
      fontFamily: 'Calibri',
      fontSize: '12px',
      border: '1px solid rgb(236, 151, 202)',
      borderRadius: '15px',
      height: '30px',
      boxShadow: 'none',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ec008c',
        boxShadow: '0 0 6px rgba(236, 0, 140, 0.4)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ec008c',
      },
    }}
  >
    <MenuItem value="" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
      --Select--
    </MenuItem>
    {names.map((item, idx) => (
      <MenuItem key={idx} value={item.field} sx={{ fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        {item.label}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            style={{ padding: '5px 5px', borderRadius: '15px', border: '1px solid rgb(236, 151, 202)', outline: 'none' , transition: 'all 0.3s ease'
      }}
      onFocus={(e) => {
        e.target.style.border = '1px solid #ec008c';
        e.target.style.boxShadow = '0 0 6px rgba(236, 0, 140, 0.4)';
      }}
      onBlur={(e) => {
        e.target.style.border = '1px solid rgb(236, 151, 202)';
        e.target.style.boxShadow = '0 1px 2px rgb(236, 151, 202)';
      }}
          />
          <button
            onClick={handleSearch}
            disabled={searchLoading}
            style={{ padding: '6px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '5px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            View All
          </button>
        </div>
      </div>
      </div>

      <div style={{ height: 'auto', width: '85%', margin: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          onRowClick={handleRowClick}
          localeText={{ noRowsLabel: 'No Records Found, Please search again.' }}                  
          sx={{
            fontSize: '12px',
            fontFamily: 'Segoe UI',
            '& .pink-header': {
              color: '#fff',
              backgroundColor: '#ec008c',
              fontWeight: 'bold',
              whiteSpace: 'normal',         // allow wrapping
              wordBreak: 'break-word',      // break long words
              overflowWrap: 'break-word',   // break long words
              lineHeight: 1.2,
              textAlign: 'center',          // optional: center header text
              padding: '8px 2px',
            },
            
            // '& .locked-task-row': {
            //   backgroundColor: 'rgb(233, 230, 230) !important',
            //   color: 'black'
            // }
             '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5 !important',
              cursor: 'pointer',
            },// Remove blue outline on cell focus
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    // Remove blue outline on column header focus
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
          }}
        />
      </div>
       
    </div>
  );
};

export default PaidTaskGrid;
